import {get, isEmpty, isNil} from 'lodash'
import {api} from 'services/api'
import theme from 'theme/index'
import {getCurrency, round} from 'helpers/financial'
import {features, PartType} from 'helpers/constants'
import {isAssembliesEnabled} from 'helpers/partContents'
import {sortByLabel} from 'helpers/sorting'
import {getPartSupplierForCompany} from 'helpers/partSuppliers'
import {getUnitCostForCostBreak} from 'helpers/partCostBreaks'
import {getPackTypeFor} from 'helpers/packTypes'
import {getBasePack, getBasePackType, getDefaultPack, getPartPackFor, getPartPacks} from 'helpers/packs'
import {
  accountHasFeature,
  checkProVersion,
  isPricingCalculatorPriceListEnabled,
  PRO_VERSION,
} from 'helpers/user'
import {
  getMaxPriceBreak,
  getMinPriceBreak,
  getPartPriceBreaks,
  getPartPriceBreaksFor,
  isUseCostBreaksFieldEnabled,
  updateAllPartPriceBreaksFor,
  updatePartPriceBreak,
  updatePartPriceBreaksFor,
} from 'helpers/priceBreaks'

export const getPartLabel = (part) => {
  const partCode = get(part, 'partCode', null)
  const description = get(part, 'description', null)
  return [partCode, description]
    .filter(v => !isEmpty(v))
    .join(' ')
}

export const getAllPartTypes = () => {
  return Object.values(PartType)
    .map(type => ({
      key: type.id,
      value: type.id,
      label: type.label,
    }))
    .sort(sortByLabel)
}

export const getEditablePartTypes = () => {
  return Object.values(PartType)
    .filter(type => canEditPartType(type.id))
    .map(type => ({
      key: type.id,
      value: type.id,
      label: type.label,
    }))
}

export const getPartTypeLabel = (id) => {
  return get(Object.values(PartType).find(part => part.id === id), 'label')
}

export {PartType}

export const isPartTypeAssembly = (partType) => {
  return partType === PartType.ASSEMBLY.id
}

export const isPartTypePricingCalculator = (partType) => {
  return partType === PartType.GO_PRICING_CALCULATOR.id
}

export const excludedPartTemplatesForOptions = [
  PartType.RESOURCE.id,
  PartType.TIMESHEET.id,
  PartType.EXPENSES.id,
  PartType.RESOURCE.id,
  PartType.PRO_CALC_WIZARD.id,
  PartType.GO_PRICING_CALCULATOR.id,
  PartType.RENTAL.id,
]

export const getPartType = (id) => {
  return Object.values(PartType).find(t => t.id === id)
}

export const PartStatus = {
  DISCONTINUED: {
    id: 0,
    label: 'Discontinued',
  },
  ACTIVE: {
    id: 1,
    label: 'Active',
  },
  COMING_SOON: {
    id: 2,
    label: 'Coming Soon',
  },
  BUY_ONLY: {
    id: 3,
    label: 'Buy Only',
  },
}

export const AllowedOrderPartStatuses = [
  PartStatus.ACTIVE.id,
]

export const AllowedPurchaseOrderPartStatuses = [
  PartStatus.ACTIVE.id,
  PartStatus.BUY_ONLY.id,
]

export const isPartAllowedToAdd = (allowedPartStatuses, part) => {
  if (isNil(part))
    return true

  const status = get(part, 'status', null)
  return allowedPartStatuses.includes(status)
}

export const getPartStatusLabel = (status) => {
  const partStatus = getPartStatus(status)
  return get(partStatus, 'label', null)
}

export const getPartStatus = (id) => {
  return Object.values(PartStatus).find(t => t.id === id)
}

export const getAllPartStatuses = () => {
  return Object.values(PartStatus)
    .map(type => ({
      key: type.id,
      value: type.id,
      label: type.label,
    }))
    .sort(sortByLabel)
}

export const getColorForPartStatus = (status) => {
  if (isNil(status))
    return ''

  switch (status) {
    case PartStatus.ACTIVE.id:
      return theme.partStatus.active
    case PartStatus.BUY_ONLY.id:
      return theme.partStatus.buyOnly
    case PartStatus.COMING_SOON.id:
      return theme.partStatus.comingSoon
    case PartStatus.DISCONTINUED.id:
      return theme.partStatus.discontinued
    default:
      return ''
  }
}

export const getClassNameForPartStatus = (status) => {
  if (isNil(status)) return ''
  switch (status) {
    case PartStatus.ACTIVE.id:
      return 'active'
    case PartStatus.BUY_ONLY.id:
      return 'buy-only'
    case PartStatus.COMING_SOON.id:
      return 'coming-soon'
    case PartStatus.DISCONTINUED.id:
      return 'discontinued'
    default:
      return ''
  }
}

export const PartAttributeDataType = {
  TEXT: {
    id: 1,
    label: 'Text',
    parseValue: (value) => value.stringValue,
  },
  DATE: {
    id: 2,
    label: 'Date',
    parseValue: (value) => value.dateTimeValue,
  },
  CURRENCY: {
    id: 3,
    label: 'Currency',
    parseValue: (value) => value.currencyValue,
  },
  INTEGER: {
    id: 4,
    label: 'Integer',
    parseValue: (value) => value.integerValue,
  },
  FRACTIONAL: {
    id: 5,
    label: 'Fractional',
    parseValue: (value) => value.floatValue,
  },
  BOOLEAN: {
    id: 6,
    label: 'Boolean',
    parseValue: (value) => !!value.integerValue,
  },
  OPTION: {
    id: 7,
    label: 'Option',
    parseValue: (value) => value.partAttributeOptionId,
  },
  OPTION_LIST: {
    id: 8,
    label: 'Option List',
    parseValue: () => null,  // TODO: Where to store this value?
  },
}

export const getPartAttributeDataType = (id) => {
  return Object.values(PartAttributeDataType).find(t => t.id === id)
}

export const nullPart = {
  category: {
    name: null,
    markup: null,
  },
  description: null,
  type: PartType.MATERIAL.id,
  status: PartStatus.ACTIVE.id,
  partCategoryId: null,
  partTemplateId: null,
  packPrice: 0,
  packDescription: 'Unit(s)',
  manCode: null,
  taxCodeId: null,
  taxCode2Id: null,
  notes: null,
  privateNotes: null,
  sizeX: 0,
  sizeY: 0,
  sizeZ: 0,
  lengthClass: null,
  weight: null,
  weightClass: null,
  nominalCode: null,
  purchaseNominalCode: null,
  msrpPricing: false,
  priceCurrencyId: null,
  purchaseDepartment: null,
  packs: [],
  attributes: [],
  contents: [],
  guid: null,
}

export const nullPartCategory = {
  createdDate: null,
  id: null,
  modifiedDate: null,
  name: null,
  parentId: null,
  partTemplateId: null,
  markup: null,
  partType: null,
  priceListId: null,
  taxCode2Id: null,
  taxCodeId: null,
  guid: null,
}

export const getPriceCurrencyId = (part) => {
  return get(part, 'priceCurrencyId', null)
}

// Result is expressed in the part's pricing currency
export const getUnitPriceForPriceBreak = (part, pack, priceBreak, pricingCurrency = null) => {
  if (priceBreak.fixedPrice) return priceBreak.fixedPrice

  let cost = get(priceBreak, 'cost', null)
  if (isNil(cost)) cost = get(pack, 'standardCost', null)

  if (!isNil(priceBreak.markup) && !isNil(cost)) {
    // Note: standardCost is expressed in the home currency
    const rate = pricingCurrency ? pricingCurrency.saleRate || pricingCurrency.rate : 1
    return round((cost + (cost * priceBreak.markup / 100)) * rate, 2)
  }

  if (part.msrpPricing && !isNil(priceBreak.discount)) {
    const discount = priceBreak.discount || 0
    const msrp = pack.msrp || 0
    return round(msrp - (msrp * discount / 100), 2)
  }

  return 0
}

export const getPackTypeName = (packTypeId, packTypes) => {
  const packType = packTypes ? packTypes.find(pt => pt.id === packTypeId) : null
  if (packType) return packType.description
  return '' // unknown
}

export const getMinPackQuantity = (pack) => {
  const minPricePacks = pack.prices.map(price => price.minPacks)
  return minPricePacks.reduce((a, b) => Math.min(a, b))
}

export const getMinSupplierQuantity = (supplier) => {
  const minPriceSupplier = supplier.costs.map(price => price.minPacks)
  return minPriceSupplier.reduce((a, b) => Math.min(a, b))
}

export const canUsePack = (pack) => {
  return pack && pack.hasOwnProperty('prices') && pack.prices.length > 0
}

export const canUseCosts = (suppliers) => {
  return suppliers && suppliers.hasOwnProperty('costs') && suppliers.costs.length > 0
}

// Returns the maximum % unit price saving for the specified price break versus a single pack quantity.
// For example, 1x is priced at £10.00, but 5x are priced at £5.00 each, thus giving a 50% saving.
// Returns a figure to 0 decimal places, intended for display purposes.
// Returns null if there is no saving, or it cannot be calculated.
export const getUnitPriceSaving = (part, pack, price, pricingCurrency) => {

  // Get the price for 1
  const singlePrice = getPartPrice(part, pack, 1, pricingCurrency)

  let unitPrice
  if (price.maxPacks > 0) {
    // Get the price for the top-end of the price break (maximum saving)
    unitPrice = getPartPrice(part, pack, price.maxPacks, pricingCurrency) / price.maxPacks
  } else if (!pack.cumulative) {
    // If no maximum, work off the minimum (cannot do this reliably for cumulative breaks)
    unitPrice = getPartPrice(part, pack, price.minPacks, pricingCurrency) / price.minPacks
  }

  // Calculate the maximum saving
  if (unitPrice && unitPrice < singlePrice) return Math.round((singlePrice - unitPrice) / singlePrice * 100)
  return null
}

// Calculates the price for a part based on quantity, assuming nominal size.
// Returns null if quantity not allowed, or cannot be calculated.
// Result is expressed in the part's pricing currency.
export const getPartPrice = (part, pack, quantity, pricingCurrency) => {
  if (part && pack && pack.prices && pack.prices.length > 0) {

    // Find the applicable price break for this quantity
    const price = getApplicablePriceBreak(pack.prices, quantity)
    if (price) {

      let ret = 0
      if (pack.cumulative) {
        // Cumulative price calculation
        let remainingQuantity = quantity
        pack.prices.forEach(p => {
          if (p.minPacks <= price.minPacks && remainingQuantity > 0) {
            let applicableQuantity = remainingQuantity
            if (p.maxPacks > 0) {
              const priceRange = (p.maxPacks - p.minPacks + 1)
              if (applicableQuantity > priceRange) applicableQuantity = priceRange
            }

            const unitPrice = getUnitPriceForPriceBreak(part, pack, p, pricingCurrency)
            ret += unitPrice * applicableQuantity
            remainingQuantity -= applicableQuantity
          }
        })
      } else {
        // Calculated from the applicable price break
        ret = getUnitPriceForPriceBreak(part, pack, price, pricingCurrency) * quantity
      }

      // Apply base or minimum price
      if (part.basePrice) {
        if (part.basePriceIsMin) {
          if (ret < part.basePrice) ret = part.basePrice
        } else {
          ret += part.basePrice
        }
      }

      // Round and return the result
      return round(ret, 2)
    }
  }

  return null
}

// Returns the maximum % unit price saving for the specified price break versus a single pack quantity.
// For example, 1x is priced at £10.00, but 5x are priced at £5.00 each, thus giving a 50% saving.
// Returns a figure to 0 decimal places, intended for display purposes.
// Returns null if there is no saving, or it cannot be calculated.
export const getUnitCostSaving = (part, pack, companyId, cost, costingCurrency) => {

  // Get the cost for 1
  const singleCost = getPartCost(part, pack, companyId, 1, costingCurrency)

  let unitCost
  if (cost.maxPacks > 0) {
    // Get the cost for the top-end of the cost break (maximum saving)
    unitCost = getPartCost(part, pack, companyId, cost.maxPacks, costingCurrency) / cost.maxPacks
  } else if (!pack.cumulative) {
    // If no maximum, work off the minimum (cannot do this reliably for cumulative breaks)
    unitCost = getPartCost(part, pack, companyId, cost.minPacks, costingCurrency) / cost.minPacks
  }

  // Calculate the maximum saving
  if (unitCost && unitCost < singleCost) return Math.round((singleCost - unitCost) / singleCost * 100)
  return null
}

// Calculates the cost for a part based on quantity, assuming nominal size.
// Returns null if quantity not allowed, or cannot be calculated.
// Result is expressed in the part supplier's cost currency.
export const getPartCost = (part, pack, companyId, quantity, costingCurrency) => {
  const partSupplier = getPartSupplierForCompany(part, pack.id, companyId)
  if (part && pack && partSupplier && partSupplier.costs && partSupplier.costs.length > 0) {

    // Find the applicable cost break for this quantity
    const cost = getApplicablePriceBreak(partSupplier.costs, quantity)
    if (cost) {

      let ret = 0
      if (pack.cumulative) {
        // Cumulative price calculation
        let remainingQuantity = quantity
        partSupplier.costs.forEach(p => {
          if (p.minPacks <= cost.minPacks && remainingQuantity > 0) {
            let applicableQuantity = remainingQuantity
            if (p.maxPacks > 0) {
              const priceRange = (p.maxPacks - p.minPacks + 1)
              if (applicableQuantity > priceRange) applicableQuantity = priceRange
            }

            const unitCost = getUnitCostForCostBreak(part, pack, p, costingCurrency)
            ret += unitCost * applicableQuantity
            remainingQuantity -= applicableQuantity
          }
        })
      } else {
        // Calculated from the applicable cost break
        ret = getUnitCostForCostBreak(part, pack, cost, costingCurrency) * quantity
      }

      // Apply base or minimum price
      if (partSupplier.baseCost) {
        if (partSupplier.baseCostIsMin) {
          if (ret < partSupplier.baseCost) ret = partSupplier.baseCost
        } else {
          ret += partSupplier.baseCost
        }
      }

      // Round and return the result
      return round(ret, 2)
    }
  }

  return null
}

// Returns the applicable price break given the quantity
export const getApplicablePriceBreak = (prices, quantity) => {
  return prices.find(price =>
    (quantity >= price.minPacks && (price.maxPacks === 0 || quantity <= price.maxPacks))
  )
}

const isServicePartsEnabled = (user) => {
  return accountHasFeature(user, features.PRICE_LISTS_LICENCE) &&
    checkProVersion(user, PRO_VERSION.v6p4p4, true)
}

export const canAddItemOfType = (type, user) => {
  switch (type) {
    case PartType.TIMESHEET.id:
    case PartType.EXPENSES.id:
    case PartType.RESOURCE.id:
    case PartType.PRO_CALC_WIZARD.id:
    case PartType.GO_PRICING_CALCULATOR.id:
    case PartType.RENTAL.id:
      return false

    case PartType.ASSEMBLY.id:
      return isAssembliesEnabled(user)

    case PartType.SERVICE.id:
      return isServicePartsEnabled(user)

    default:
      return accountHasFeature(user, features.PRICE_LISTS_LICENCE)
  }
}

const defaultAllowedTypes = [
  PartType.MATERIAL.id,
  PartType.NON_STOCK.id,
  PartType.OUTSOURCED.id,
  PartType.SERVICE.id,
]

export const canAddMaterial = (type, status, user, allowedTypes) => {
  if (isNil(allowedTypes))
    allowedTypes = defaultAllowedTypes

  const isActive = (status === PartStatus.ACTIVE.id)
  const isAllowedType = allowedTypes.includes(type)
  return isActive && isAllowedType && accountHasFeature(user, features.PRICE_LISTS_LICENCE)
}

export const isPartTypeAllowedForPurchaseOrder = (type) => {
  const disallowedTypes = [
    PartType.LABOUR.id,
    PartType.SERVICE.id,
    PartType.RESOURCE.id,
    PartType.ASSEMBLY.id,
    PartType.PRO_CALC_WIZARD.id,
  ]
  return !disallowedTypes.includes(type)
}

export const canEditPartType = (type) => {
  switch (type) {
    case PartType.TIMESHEET.id:
    case PartType.EXPENSES.id:
    case PartType.PRO_CALC_WIZARD.id:
    case PartType.GO_PRICING_CALCULATOR.id:
    case PartType.RENTAL.id:
    case PartType.ASSEMBLY.id:
      return false
    default:
      return true
  }
}

export const canSelectPart = (user, partType) => {
  switch (partType) {
    case PartType.TIMESHEET.id:
    case PartType.EXPENSES.id:
    case PartType.PRO_CALC_WIZARD.id:
    case PartType.RENTAL.id:
      return false
    case PartType.ASSEMBLY.id:
      return isAssembliesEnabled(user)
    case PartType.GO_PRICING_CALCULATOR.id:
      return isPricingCalculatorPriceListEnabled(user)
    default:
      return true
  }
}

const defaultPacksFieldOptions = {
  default: true,
  prices: true,
  suppliers: {
    default: true,
    company: {
      name: true,
    },
  },
}

export const searchPartsForQuote = (valueField, user, packsFieldOptions = defaultPacksFieldOptions) => (search) => {
  if (search && search.length > 2) {
    return api.parts.get({search, searchField: valueField}, {
      default: true,
      partImages: true,
      packs: packsFieldOptions,
      category: true,
      attributes: true,
      partCode: true,
      taxCode: true,
    }).then(response => {
      let ret = []
      api.handleResponseNonYield(
        response,
        (key, data) => {
          ret = data.result
        },
        (key, message) => {
          console.error(message)
        }
      )
      return ret.map(part => ({
        value: part[valueField],
        label: `${part.partCode}: ${part.description}`,
        part,
        disabled: !canAddItemOfType(part.type, user),
      }))
    })
  }
  return []
}

// Deprecated
export const PartSubType = {
  Stock: 0,
  Non_Stock: 1,
  Labour: 2,
  Resource: 3,
  Outsourced: 4,
  Assembly: 5,
}

// Deprecated
export const getPartSubTypeItems = () => {
  return [
    {
      value: PartSubType.Stock,
      label: 'Material (Stock)',
    },
    {
      value: PartSubType.Non_Stock,
      label: 'Material (Non Stock)',
    },
    {
      value: PartSubType.Labour,
      label: 'Labour',
    },
    {
      value: PartSubType.Resource,
      label: 'Labour (Resource)',
    },
    {
      value: PartSubType.Outsourced,
      label: 'Labour (Outsourced)',
    },
    {
      value: PartSubType.Assembly,
      label: 'Assembly',
    },
  ]
}

export const updatePart = (part, key, value) => {
  switch (key) {
    case 'basePackType':
      break
    default:
      part[key] = value
      break
  }

  return part
}

export const getPrice = (part, partPack, partPriceBreak, currencies) => {
  if (!isNil(get(partPriceBreak, 'fixedPrice', null))) return partPriceBreak?.fixedPrice

  const priceCurrencyRate = getPartSaleRate(part, currencies)

  if (!isNil(get(partPriceBreak, 'markup', null)))
    return calculatePriceFromMarkup(getCost(partPack, partPriceBreak), partPriceBreak?.markup, priceCurrencyRate)

  if (part?.msrpPricing)
    return calculatePriceFromMSRP(partPack?.msrp || 0, partPriceBreak?.salesDiscount || 0, priceCurrencyRate)

  return 0
}

export const getCost = (partPack, partPriceBreak) => {
  if (!isNil(get(partPriceBreak, 'cost', null))) return partPriceBreak.cost
  return get(partPack, 'standardCost', 0)
}

export const getSalesDiscount = (part, partPack, partPriceBreak, currencies) => {
  if (!isNil(partPriceBreak?.salesDiscount)) return partPriceBreak?.salesDiscount
  return calculateSalesDiscountForFixedPrice(part, partPack, partPriceBreak, currencies)
}

export const getMarkup = (part, partPack, partPriceBreak, currencies) => {
  if (!isNil(get(partPriceBreak, 'markup', null))) return partPriceBreak.markup

  const priceCurrencyRate = getPartSaleRate(part, currencies)
  return calculateMarkup(
    getCost(partPack, partPriceBreak),
    getPrice(part, partPack, partPriceBreak, currencies),
    priceCurrencyRate)
}

export const calculatePriceFromMarkup = (cost, markup, currencyRate = 1) =>
  (cost + (cost * markup / 100)) * currencyRate
export const calculatePriceFromMSRP = (msrp, discount, currencyRate = 1) =>
  (msrp - (msrp * discount / 100)) * currencyRate

export const calculateSalesDiscountForFixedPrice = (part, partPack, partPriceBreak, currencies) => {
  if (part?.msrpPricing) {
    if (!isNil(partPriceBreak?.fixedPrice)) return 0
    const msrp = get(partPack, 'msrp', 0)
    const priceCurrencyRate = getPartSaleRate(part, currencies)
    return calculateSalesDiscount(msrp, getPrice(part, partPack, partPriceBreak, currencies), priceCurrencyRate)
  }
  return null
}

export const calculateSalesDiscount = (msrp, price, priceCurrencyRate = 1) => {
  return msrp === 0 ? 100 : (msrp - (price / priceCurrencyRate)) / msrp * 100
}

export const calculateMarkup = (cost, price, priceCurrencyRate = 1) => {
  return cost === 0 ? 100 : ((price / priceCurrencyRate) - cost) / cost * 100
}

export const updatePartPrice = (part, partPriceBreak, key) => {
  if (key) {
    switch (key) {
      case 'fixedPrice': {
        partPriceBreak.salesDiscount = null
        partPriceBreak.markup = null
        break
      }
      case 'salesDiscount': {
        if (part.msrpPricing) {
          partPriceBreak.fixedPrice = null
          partPriceBreak.markup = null
        } else {
          // Not in use
          partPriceBreak.salesDiscount = null
        }
        break
      }
      case 'markup': {
        partPriceBreak.fixedPrice = null
        partPriceBreak.salesDiscount = null
        break
      }
    }
  }
}

export const getPriceSummaryFor = (part, packTypes, currency, isComplexPricingEnabled, companyId, currencies) => {
  const partPacks = get(part, 'packs', [])
  const basePartPack = getBasePack(partPacks)
  const basePackType = getBasePackType(basePartPack.packTypeId, packTypes)
  const partPriceBreaks = getPartPriceBreaksFor(basePartPack.id, part, isComplexPricingEnabled, companyId)
  const minPriceBreak = getMinPriceBreak(partPriceBreaks)
  const minPriceBreakPrice = getPrice(part, basePartPack, minPriceBreak, currencies)
  const maxPriceBreak = getMaxPriceBreak(partPriceBreaks)
  const maxPriceBreakPrice = getPrice(part, basePartPack, maxPriceBreak, currencies)
  return `${minPriceBreakPrice} ${currency.shortDesc} - ${maxPriceBreakPrice} ${currency.shortDesc} / ${basePackType.description}`
}

export const getCostSummaryFor = (part, packTypes, currency, isComplexPricingEnabled) => {
  const partPacks = getPartPacks(part, isComplexPricingEnabled)
  const defaultPartPack = getDefaultPack(partPacks)
  const packTypeId = get(defaultPartPack, 'packTypeId', null)
  const packType = getPackTypeFor(packTypeId, packTypes)
  return `${defaultPartPack.standardCost} ${currency.shortDesc} / ${packType.description}`
}

export const parsePart = (part) => {
  // Nothing to do here at present
  return part
}

export const getNominalCodeDescription = (nominalCodeCode, nominalCodes = []) => {
  if (isNil(nominalCodeCode)) return null
  const nominalCode = nominalCodes.find(nominalCode => nominalCode.code === nominalCodeCode)
  return !isNil(nominalCode)
    ? `${nominalCode.code}: ${nominalCode.desc}`
    : !isNil(nominalCodeCode)
      ? nominalCodeCode
      : null
}

export const filterChangeablePartTypesFromPartTemplate = (partTypeId) => {
  return [
    ...PartChangeableDefinitions[partTypeId] ? PartChangeableDefinitions[partTypeId] : [],
    partTypeId,
  ].filter(id => excludedPartTemplatesForOptions.indexOf(id) === -1)
}

export const isPartTemplateReadOnly = (partTypeId) => {
  return !filterChangeablePartTypesFromPartTemplate(partTypeId).length > 1
}

export const getChangeablePartTemplates = (partType, partTemplates = []) => {
  const partTypeIds = filterChangeablePartTypesFromPartTemplate(partType)
  return partTemplates.filter(template => partTypeIds.indexOf(template.partType) !== -1)
    .map((option) => ({key: option.id, id: option.id, label: option.name}))
}

export const PartChangeableDefinitions = {
  [PartType.MATERIAL.id]: [
    PartType.OUTSOURCED.id,
    PartType.NON_STOCK.id,
  ],
  [PartType.LABOUR.id]: [
    PartType.RESOURCE.id,
  ],
  [PartType.RESOURCE.id]: [
    PartType.LABOUR.id,
  ],
  [PartType.OUTSOURCED.id]: [
    PartType.MATERIAL.id,
    PartType.NON_STOCK.id,
  ],
  [PartType.NON_STOCK.id]: [
    PartType.MATERIAL.id,
    PartType.OUTSOURCED.id,
  ],
}

export const getPartClassShortCode = (part, options, key) => {
  return isNil(get(part, key, null))
    ? null
    : get(options.find(option => option.id === part[key]), 'shortCode', null)
}

export const getPartPropertyWithLengthClass = (part, key) => {
  const property = get(part, key, 0)
  const lengthClass = getPartClassShortCode(part, getLengthClasses(), 'lengthClass')
  return [property, lengthClass]
    .filter(value => !isNil(value))
    .join('')
}

export const getLengthClassOptions = (e) => {
  const part = get(e, 'part', null)
  const partClassShortCode = getPartClassShortCode(part, getLengthClasses(), 'lengthClass')
  return {
    accuracy: 0,
    append: partClassShortCode,
  }
}

export const getPartPropertyWithWeightClass = (part, defaultValue = 0) => {
  const weight = get(part, 'weight', defaultValue)
  const weightClass = getPartClassShortCode(part, getWeightClasses(), 'weightClass')
  return [weight, weightClass]
    .filter(value => !isNil(value))
    .join('')
}

export const LengthClass = {
  METER: 0,
  CENTIMETRE: 1,
  MILLIMETRE: 2,
  INCH: 3,
}

export const getLengthClasses = () => [
  {
    id: LengthClass.METER,
    key: LengthClass.METER,
    value: LengthClass.METER,
    label: 'Meter',
    shortCode: 'm',
  },
  {
    id: LengthClass.CENTIMETRE,
    key: LengthClass.CENTIMETRE,
    value: LengthClass.CENTIMETRE,
    label: 'Centimetre',
    shortCode: 'cm',
  },
  {
    id: LengthClass.MILLIMETRE,
    key: LengthClass.MILLIMETRE,
    value: LengthClass.MILLIMETRE,
    label: 'Millimetre',
    shortCode: 'mm',
  },
  {
    id: LengthClass.INCH,
    key: LengthClass.INCH,
    value: LengthClass.INCH,
    label: 'Inch',
    shortCode: '"',
  },
]

/**
 * Converts a length value to millimeters based on the lengthClass
 * @param {number} value - The length value to convert
 * @param {number} lengthClass - The LengthClass constant (0=meter, 1=cm, 2=mm, 3=inch)
 * @returns {number} The value converted to millimeters
 */
export const convertToMillimeters = (value, lengthClass) => {
  if (isNil(value) || isNil(lengthClass)) return value

  switch (lengthClass) {
    case LengthClass.METER:
      return value * 1000
    case LengthClass.CENTIMETRE:
      return value * 10
    case LengthClass.MILLIMETRE:
      return value
    case LengthClass.INCH:
      return value * 25.4
    default:
      return value
  }
}

export const WeightClass = {
  TONNE: 0,
  KILOGRAM: 1,
  GRAM: 2,
  POUND: 3,
  OUNCE: 4,
}

export const getWeightClasses = () => [
  {
    id: WeightClass.TONNE,
    key: WeightClass.TONNE,
    value: WeightClass.TONNE,
    label: 'Tonne',
    shortCode: 't',
  },
  {
    id: WeightClass.KILOGRAM,
    key: WeightClass.KILOGRAM,
    value: WeightClass.KILOGRAM,
    label: 'Kilogram',
    shortCode: 'kg',
  },
  {
    id: WeightClass.GRAM,
    key: WeightClass.GRAM,
    value: WeightClass.GRAM,
    label: 'Gram',
    shortCode: 'g',
  },
  {
    id: WeightClass.POUND,
    key: WeightClass.POUND,
    value: WeightClass.POUND,
    label: 'Pound',
    shortCode: 'lbs',
  },
  {
    id: WeightClass.OUNCE,
    key: WeightClass.OUNCE,
    value: WeightClass.OUNCE,
    label: 'Ounce',
    shortCode: 'oz',
  },
]

export const enableUseCostBreaks = (part, partPackId, isComplexPricingEnabled) => {
  if (!isUseCostBreaksFieldEnabled(part, partPackId)) {
    const partPacks = getPartPacks(part, isComplexPricingEnabled)
    const partPack = getPartPackFor(partPackId, partPacks)
    const prices = getPartPriceBreaks(partPack, isComplexPricingEnabled)
    const minPriceBreak = getMinPriceBreak(prices)
    const partPriceBreakId = get(minPriceBreak, 'id', null)
    const standardCost = get(partPack, 'standardCost', 0)
    return updatePartPriceBreaksFor(partPackId, partPriceBreakId, 'cost', standardCost, part)
  }

  return part
}

export const disableUseCostBreaks = (part, partPackId, isComplexPricingEnabled) => {
  const partPack = getPartPackFor(partPackId, part.packs)
  const prices = getPartPriceBreaks(partPack, isComplexPricingEnabled)
  const updatedPrices = prices.map(price => updatePartPriceBreak(part, price, 'cost', null))
  return updateAllPartPriceBreaksFor(partPackId, updatedPrices, part)
}

export const getPartSaleRate = (part, currencies) => {
  if (part && currencies) {
    const pricingCurrency = getCurrency(part?.priceCurrencyId, currencies)
    if (pricingCurrency) return pricingCurrency?.saleRate || pricingCurrency?.rate
  }
  return 1
}

export const getCategoryName = (value) => {
  const templateItem = get(value, 'templateItem', null)
  if (isEmpty(templateItem)) {
    return 'From Price List / Quick Add'
  }
  return `${get(value, 'templateItem.name', '')} (select ${get(value, 'templateItem.multiple')
    ? 'ANY' : 'ONE'})${get(value, 'templateItem.mandatory') ? ' [MANDATORY]' : ''}`

}

export default {
  PartSubType,
  getPartSubTypeItems,
  getPrice,
  getCost,
  getMarkup,
  getSalesDiscount,
  updatePartPrice,
  calculatePriceFromMarkup,
  calculatePriceFromMSRP,
  calculateMarkup,
  calculateSalesDiscount,
  excludedPartTemplatesForOptions,
}
